import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

const controlClass = "flex h-10 w-full border border-input bg-background px-3 py-2 text-sm rounded-none";
const sameSchool = (entry, id) => !entry.school_id || entry.school_id === id;
const today = () => {
  const value = new Date();
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 10);
};

export function copyDestinationOptions({ context, schools, semesters, stages, disciplines, classes, draft }) {
  if (!context) return { schools: [], semesters: [], stages: [], disciplines: [], classes: [], defaultClassIds: [], canGlobal: false, schoolManager: false, teacherOnly: false };
  const teachers = context.teacher_assignments || [];
  const managers = context.stage_manager_assignments || [];
  const managedSchools = context.school_manager_school_ids || [];
  const schoolOptions = schools.filter((school) => context.is_admin || managedSchools.includes(school.id)
    || teachers.some((entry) => entry.school_id === school.id)
    || managers.some((entry) => entry.school_id === school.id));
  const schoolManager = Boolean(context.is_admin || managedSchools.includes(draft.school_id));
  const schoolTeachers = teachers.filter((entry) => sameSchool(entry, draft.school_id));
  const schoolManagers = managers.filter((entry) => sameSchool(entry, draft.school_id));
  const semesterOptions = semesters.filter((semester) => sameSchool(semester, draft.school_id)
    && (schoolManager || schoolTeachers.some((entry) => entry.semester_id === semester.id)
      || schoolManagers.some((entry) => !entry.semester_id || entry.semester_id === semester.id)));
  const teacherAssignments = schoolTeachers.filter((entry) => entry.semester_id === draft.semester_id);
  const managerAssignments = schoolManagers.filter((entry) => !entry.semester_id || entry.semester_id === draft.semester_id);
  const stageOptions = stages.filter((stage) => sameSchool(stage, draft.school_id)
    && (stage.matrix_id || stage.semester_id === draft.semester_id)
    && (schoolManager || teacherAssignments.some((entry) => entry.stage_id === stage.id)
      || managerAssignments.some((entry) => entry.stage_id === stage.id)));
  const canGlobal = Boolean(schoolManager || managerAssignments.some((entry) => entry.stage_id === draft.stage_id));
  const teacherOnly = !canGlobal;
  const disciplineOptions = disciplines.filter((discipline) => discipline.stage_id === draft.stage_id
    && sameSchool(discipline, draft.school_id)
    && (canGlobal || teacherAssignments.some((entry) => entry.discipline_id === discipline.id && entry.stage_id === draft.stage_id)));
  const stage = stageOptions.find((item) => item.id === draft.stage_id);
  const assignment = teacherAssignments.find((entry) => entry.discipline_id === draft.discipline_id && entry.stage_id === draft.stage_id);
  const defaultClassIds = (teacherOnly && assignment?.class_ids?.length ? assignment.class_ids : stage?.class_ids) || [];
  const stageClassIds = stage?.class_ids || [];
  const classOptions = classes.filter((item) => item.school_id === draft.school_id
    && (draft.school_wide || draft.academic_scope === "legacy" || stageClassIds.includes(item.id))
    && (!teacherOnly || draft.allow_exceptional_classes || defaultClassIds.includes(item.id)));
  return {
    schools: schoolOptions, semesters: semesterOptions, stages: stageOptions, disciplines: disciplineOptions,
    classes: classOptions, defaultClassIds, canGlobal, schoolManager, teacherOnly,
  };
}

function Field({ label, id, children }) {
  return <div><label htmlFor={id} className="label-eyebrow mb-1 block">{label}</label>{children}</div>;
}

export default function ExamCopyDialog({ exam, schools = [], classes = [], semesters = [], disciplines = [], creationContext, onClose, onCopied }) {
  const [draft, setDraft] = useState(() => ({
    name: `Cópia de ${exam.name || "prova"}`.slice(0, 140), exam_date: today(),
    school_id: exam.school_id || (exam.school_ids?.length === 1 ? exam.school_ids[0] : ""),
    semester_id: exam.semester_id || "", stage_id: exam.stage_id || "",
    discipline_id: exam.discipline_id || "", academic_scope: exam.academic_scope || "discipline",
    class_ids: [], school_wide: false, allow_exceptional_classes: false,
  }));
  const [stages, setStages] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(true);
  const [scopeError, setScopeError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const classSelectionKey = useRef("");
  const options = useMemo(() => copyDestinationOptions({
    context: creationContext, schools, semesters, stages, disciplines, classes, draft,
  }), [creationContext, schools, semesters, stages, disciplines, classes, draft]);

  useEffect(() => {
    if (!creationContext) return;
    if (!options.schools.some((item) => item.id === draft.school_id)) {
      const id = options.schools.length === 1 ? options.schools[0].id : "";
      if (draft.school_id !== id) setDraft((value) => ({ ...value, school_id: id, semester_id: "", stage_id: "", discipline_id: "", class_ids: [] }));
      return;
    }
    if (!options.semesters.some((item) => item.id === draft.semester_id)) {
      const id = options.semesters.find((item) => item.is_current)?.id || options.semesters[0]?.id || "";
      if (draft.semester_id !== id) setDraft((value) => ({ ...value, semester_id: id, stage_id: "", discipline_id: "", class_ids: [] }));
    }
  }, [creationContext, draft.school_id, draft.semester_id, options.schools, options.semesters]);

  useEffect(() => {
    let current = true;
    setStages([]);
    setScopeError("");
    if (!draft.school_id || !draft.semester_id) {
      setStagesLoading(false);
      return () => { current = false; };
    }
    setStagesLoading(true);
    const params = new URLSearchParams({ school_id: draft.school_id, semester_id: draft.semester_id });
    api.get(`/academic-stages?${params}`)
      .then((response) => { if (current) setStages(response.data || []); })
      .catch((error) => { if (current) setScopeError(fmtError(error)); })
      .finally(() => { if (current) setStagesLoading(false); });
    return () => { current = false; };
  }, [draft.school_id, draft.semester_id]);

  useEffect(() => {
    if (stagesLoading || scopeError || !draft.school_id || !draft.semester_id) return;
    let stageId = draft.stage_id;
    if (!options.stages.some((item) => item.id === stageId)) {
      stageId = !draft.school_wide && draft.academic_scope !== "legacy" && options.stages.length === 1 ? options.stages[0].id : "";
    }
    let scope = draft.academic_scope;
    if (scope === "legacy" && !options.schoolManager) scope = "discipline";
    if (scope === "global" && stageId === draft.stage_id && !options.canGlobal) scope = "discipline";
    if (stageId !== draft.stage_id || scope !== draft.academic_scope) {
      setDraft((value) => ({ ...value, stage_id: stageId, academic_scope: scope, discipline_id: "", class_ids: [], school_wide: false }));
      return;
    }
    if (scope === "discipline" && stageId && !options.disciplines.some((item) => item.id === draft.discipline_id)) {
      const id = options.disciplines.length === 1 ? options.disciplines[0].id : "";
      if (id !== draft.discipline_id) setDraft((value) => ({ ...value, discipline_id: id, class_ids: [] }));
    }
  }, [stagesLoading, scopeError, draft.school_id, draft.semester_id, draft.stage_id, draft.discipline_id,
    draft.school_wide, draft.academic_scope, options.stages, options.disciplines, options.canGlobal, options.schoolManager]);

  useEffect(() => {
    if (stagesLoading || scopeError || (draft.academic_scope === "discipline" && !draft.discipline_id)) return;
    const key = [draft.school_id, draft.semester_id, draft.stage_id, draft.discipline_id, draft.academic_scope, draft.school_wide].join("|");
    if (classSelectionKey.current === key) return;
    classSelectionKey.current = key;
    const allowed = new Set(options.classes.map((item) => item.id));
    const sourceIds = (exam.class_ids || [exam.class_id]).filter((id) => allowed.has(id));
    const defaults = options.defaultClassIds.filter((id) => allowed.has(id));
    const next = draft.school_wide ? [] : (sourceIds.length ? sourceIds : defaults);
    setDraft((value) => ({ ...value, class_ids: next }));
  }, [stagesLoading, scopeError, draft.school_id, draft.semester_id, draft.stage_id, draft.discipline_id,
    draft.academic_scope, draft.school_wide, options.classes, options.defaultClassIds, exam]);

  const change = (field, value) => {
    setDraft((current) => {
      const next = { ...current, [field]: value };
      if (["school_id", "semester_id"].includes(field)) {
        if (field === "school_id") next.semester_id = "";
        next.stage_id = ""; next.discipline_id = ""; next.class_ids = [];
        next.school_wide = false; next.allow_exceptional_classes = false;
      }
      if (["stage_id", "academic_scope"].includes(field)) {
        next.discipline_id = ""; next.class_ids = []; next.school_wide = false;
        next.allow_exceptional_classes = false;
        if (field === "academic_scope" && value === "legacy") next.stage_id = "";
      }
      if (field === "discipline_id") { next.class_ids = []; next.allow_exceptional_classes = false; }
      return next;
    });
  };
  const ready = Boolean(creationContext && !stagesLoading && !scopeError && draft.name.trim() && draft.exam_date
    && options.schools.some((item) => item.id === draft.school_id)
    && options.semesters.some((item) => item.id === draft.semester_id)
    && (draft.academic_scope === "legacy" ? options.schoolManager
      : draft.academic_scope === "global" ? options.canGlobal && (draft.stage_id || draft.school_wide)
        : draft.stage_id && options.disciplines.some((item) => item.id === draft.discipline_id)));

  const submit = async (event) => {
    event.preventDefault();
    if (!ready || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const response = await api.post(`/exams/${exam.id}/copy`, {
        ...draft, name: draft.name.trim(),
        stage_id: draft.school_wide || draft.academic_scope === "legacy" ? null : draft.stage_id || null,
        discipline_id: draft.academic_scope === "discipline" ? draft.discipline_id : null,
        class_ids: draft.school_wide ? [] : draft.class_ids,
      });
      toast.success("Prova copiada. Nenhuma resposta de aluno foi transferida.");
      onCopied(response.data);
      onClose();
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !savingRef.current) onClose(); }}>
      <DialogContent className="rounded-none max-w-3xl max-h-[92dvh] overflow-y-auto overscroll-contain">
        <DialogHeader><DialogTitle className="font-display text-3xl font-black tracking-tight">Copiar prova</DialogTitle></DialogHeader>
        <DialogDescription>
          Copia questões, ordem das alternativas, gabarito, imagens, créditos e percepção. A nova prova será sua, com novo QR Code, sem resultados nem compartilhamentos da original.
        </DialogDescription>
        <div className="border border-border bg-muted/20 p-3 text-sm"><b>Original:</b> {exam.name}<br />{exam.question_count || 100} questões · {exam.perception_count ?? 9} perguntas de percepção · mesmo layout de cartão</div>
        <form onSubmit={submit} className="space-y-4" data-testid="exam-copy-form">
          <fieldset disabled={saving} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nome da nova prova" id="copy-exam-name"><Input id="copy-exam-name" data-testid="copy-exam-name" className="rounded-none" value={draft.name} maxLength={140} onChange={(event) => change("name", event.target.value)} required /></Field>
              <Field label="Data de realização" id="copy-exam-date"><Input id="copy-exam-date" data-testid="copy-exam-date" type="date" className="rounded-none" value={draft.exam_date} onChange={(event) => change("exam_date", event.target.value)} required /></Field>
              <Field label="Escola de destino" id="copy-exam-school"><select id="copy-exam-school" data-testid="copy-exam-school" className={controlClass} value={draft.school_id} onChange={(event) => change("school_id", event.target.value)} required>
                <option value="">Selecione…</option>{options.schools.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select></Field>
              <Field label="Semestre de destino" id="copy-exam-semester"><select id="copy-exam-semester" data-testid="copy-exam-semester" className={controlClass} value={draft.semester_id} onChange={(event) => change("semester_id", event.target.value)} required>
                <option value="">Selecione…</option>{options.semesters.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_current ? " · atual" : ""}</option>)}
              </select></Field>
              <Field label="Etapa" id="copy-exam-stage"><select id="copy-exam-stage" data-testid="copy-exam-stage" className={controlClass} value={draft.stage_id} disabled={stagesLoading || draft.school_wide || draft.academic_scope === "legacy"} onChange={(event) => change("stage_id", event.target.value)}>
                <option value="">{stagesLoading ? "Carregando…" : "Selecione…"}</option>{options.stages.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select></Field>
              <Field label="Tipo acadêmico" id="copy-exam-scope"><select id="copy-exam-scope" data-testid="copy-exam-scope" className={controlClass} value={draft.academic_scope} onChange={(event) => change("academic_scope", event.target.value)}>
                <option value="discipline">Prova de disciplina</option>
                {options.canGlobal && <option value="global">Global da etapa / simulado</option>}
                {options.schoolManager && <option value="legacy">Sem etapa (legada)</option>}
              </select></Field>
            </div>
            {draft.academic_scope === "discipline" && <Field label="Disciplina" id="copy-exam-discipline"><select id="copy-exam-discipline" data-testid="copy-exam-discipline" className={controlClass} value={draft.discipline_id} onChange={(event) => change("discipline_id", event.target.value)} required>
              <option value="">Selecione…</option>{options.disciplines.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select></Field>}
            {draft.academic_scope === "global" && options.schoolManager && <label className="flex gap-2 border border-primary/30 bg-primary/5 p-3 text-sm">
              <input type="checkbox" data-testid="copy-exam-school-wide" checked={draft.school_wide} onChange={(event) => setDraft((value) => ({ ...value, school_wide: event.target.checked, class_ids: [] }))} />
              Aplicar à escola toda
            </label>}
            {options.teacherOnly && draft.discipline_id && <label className="flex gap-2 text-sm">
              <input type="checkbox" data-testid="copy-exam-exceptional" checked={draft.allow_exceptional_classes} onChange={(event) => setDraft((value) => ({ ...value, allow_exceptional_classes: event.target.checked, class_ids: event.target.checked ? value.class_ids : options.defaultClassIds }))} />
              Escolher excepcionalmente outra turma da etapa
            </label>}
            <div>
              <div className="label-eyebrow mb-2">Turmas de destino · selecione uma ou mais</div>
              <div className="border border-border p-3 grid gap-2 max-h-52 overflow-y-auto" data-testid="copy-exam-classes">
                {options.classes.map((item) => <label key={item.id} className="flex gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid={`copy-exam-class-${item.id}`} disabled={draft.school_wide} checked={draft.school_wide || draft.class_ids.includes(item.id)} onChange={() => setDraft((value) => ({ ...value, class_ids: value.class_ids.includes(item.id) ? value.class_ids.filter((id) => id !== item.id) : [...value.class_ids, item.id] }))} />{item.name}
                </label>)}
                {!options.classes.length && <div className="text-xs text-muted-foreground">Selecione a escola, o semestre e a disciplina/etapa para carregar as turmas.</div>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Sem marcação, são usadas as turmas padrão da disciplina ou etapa, conforme as regras de criação de provas.</p>
            </div>
          </fieldset>
          {!creationContext && <p role="status" className="text-sm">Carregando permissões de criação…</p>}
          {creationContext && !options.schools.length && <p role="alert" className="text-sm text-destructive">Você não tem uma alocação que permita criar provas. Peça ao gestor para conferir sua escola, semestre e disciplina.</p>}
          {scopeError && <p role="alert" className="text-sm text-destructive">Não foi possível carregar o destino: {scopeError}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-none" disabled={saving} onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="rounded-none" data-testid="copy-exam-submit" disabled={!ready || saving}>
              {saving ? <Spinner className="mr-2 animate-spin" /> : <Copy className="mr-2" />}{saving ? "Copiando…" : "Criar cópia"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
